export interface ISolSubmissionResult {
  success: boolean;
  serviceCodes?: string[] | null;
}

export interface IResolveSolSubmissionMessageParams {
  result: ISolSubmissionResult;
  te: (key: string) => boolean;
  t: (key: string) => string;
  successFallbackKey: string;
  failureFallbackKey: string;
}

const DEFAULT_NESTED_RESULT_KEYS = [
  "pickUpService",
  "deliverService",
  "assistanceService",
  "supplyMaterialService",
];

const toRecord = (source: unknown): Record<string, unknown> => {
  if (!source || typeof source !== "object") {
    return {};
  }

  return source as Record<string, unknown>;
};

const extractResultFromSource = (source: unknown): ISolSubmissionResult => {
  const data = toRecord(source);
  return {
    success: typeof data.success === "boolean" ? data.success : false,
    serviceCodes: Array.isArray(data.serviceCodes) ? (data.serviceCodes as string[]) : null,
  };
};

export const extractSolSubmissionResult = (
  source: unknown,
  nestedKeys: string[] = DEFAULT_NESTED_RESULT_KEYS
): ISolSubmissionResult => {
  const data = toRecord(source);
  for (const key of nestedKeys) {
    const nested = toRecord(data[key]);
    if (typeof nested.success === "boolean") {
      return extractResultFromSource(nested);
    }
  }

  return extractResultFromSource(source);
};

export const isSolSubmissionSuccess = (result: ISolSubmissionResult): boolean => result.success;

const resolveTranslation = (
  key: string,
  te: (key: string) => boolean,
  t: (key: string) => string
): string => {
  if (!key || !te(key)) {
    return t(key);
  }

  const message = t(key);
  return message && message.trim() !== "" ? message : t(key);
};

export const resolveSolSubmissionMessage = ({
  result,
  te,
  t,
  successFallbackKey,
  failureFallbackKey,
}: IResolveSolSubmissionMessageParams): string => {
  return isSolSubmissionSuccess(result)
    ? resolveTranslation(successFallbackKey, te, t)
    : resolveTranslation(failureFallbackKey, te, t);
};
